import "@shopify/ui-extensions/preact";
import { render } from "preact";
import { useEffect, useState } from "preact/hooks";
import {
  useAttributes,
  useBuyerJourneyActiveStep,
  useCheckoutToken,
  useCurrency,
  useDiscountCodes,
  useLanguage,
  useLocalizationCountry,
  useNote,
  useSettings,
  useShippingAddress,
} from "@shopify/ui-extensions/checkout/preact";
import { BACKEND_URL_FROM_ENV } from "./backend-url.generated";

const EXTENSION_SOURCE = "checkout-ui-1";
const EXTENSION_TARGET = "purchase.checkout.block.render";
const DEFAULT_UI_SETTINGS = {
  headingText: "You might also like",
  headingFontSize: 20,
  buttonVariant: "secondary",
  buttonTone: "auto",
};

function getExtensionUiSettings(settings) {
  const headingText =
    typeof settings?.upsell_heading === "string" &&
    settings.upsell_heading.trim()
      ? settings.upsell_heading.trim()
      : DEFAULT_UI_SETTINGS.headingText;
  const fontSize = Number(settings?.upsell_heading_font_size);
  const headingFontSize =
    Number.isFinite(fontSize) && fontSize > 0
      ? Math.max(10, Math.min(48, Math.trunc(fontSize)))
      : DEFAULT_UI_SETTINGS.headingFontSize;
  const buttonVariant =
    settings?.upsell_button_variant === "primary" ||
    settings?.upsell_button_variant === "secondary"
      ? settings.upsell_button_variant
      : DEFAULT_UI_SETTINGS.buttonVariant;
  const buttonTone =
    settings?.upsell_button_tone === "neutral" ||
    settings?.upsell_button_tone === "critical" ||
    settings?.upsell_button_tone === "auto"
      ? settings.upsell_button_tone
      : DEFAULT_UI_SETTINGS.buttonTone;

  return {
    headingText,
    headingFontSize,
    buttonVariant,
    buttonTone,
  };
}

function getHeadingType(fontSize) {
  if (fontSize <= 14) return "small";
  if (fontSize <= 17) return "strong";
  return null;
}

function getCustomerContext() {
  const customer = shopify.buyerIdentity?.customer?.value;
  const email = shopify.buyerIdentity?.email?.value || customer?.email;

  return {
    customerEmail: email || null,
    customerName:
      customer?.fullName ||
      [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") ||
      null,
    customerId: customer?.id || null,
  };
}

function toSerializable(value, seen = new WeakSet(), depth = 0) {
  if (
    value == null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value ?? null;
  }

  if (typeof value === "function" || typeof value === "symbol") {
    return undefined;
  }

  if (depth > 6) {
    return "[Max depth reached]";
  }

  if (typeof value === "object") {
    if (seen.has(value)) {
      return "[Circular]";
    }

    seen.add(value);

    if (Array.isArray(value)) {
      return value.map((item) => toSerializable(item, seen, depth + 1));
    }

    return Object.entries(value).reduce((result, [key, entry]) => {
      const serialized = toSerializable(entry, seen, depth + 1);
      if (serialized !== undefined) {
        result[key] = serialized;
      }

      return result;
    }, {});
  }

  return String(value);
}

function serializeCartLine(line) {
  const merchandise = line.merchandise;

  return {
    id: line.id,
    quantity: line.quantity,
    cost: toSerializable(line.cost),
    attributes: toSerializable(line.attributes ?? []),
    discountAllocations: toSerializable(line.discountAllocations ?? []),
    lineComponents: toSerializable(line.lineComponents ?? []),
    parentRelationship: toSerializable(line.parentRelationship),
    merchandise: merchandise
      ? {
          id: merchandise.id || null,
          type: merchandise.type || null,
          title: merchandise.title || null,
          subtitle: merchandise.subtitle || null,
          sku: merchandise.sku || null,
          requiresShipping: merchandise.requiresShipping ?? null,
          image: toSerializable(merchandise.image),
          selectedOptions: toSerializable(merchandise.selectedOptions ?? []),
          sellingPlan: toSerializable(merchandise.sellingPlan),
          product: merchandise.product
            ? {
                id: merchandise.product.id || null,
                vendor: merchandise.product.vendor || null,
                productType: merchandise.product.productType || null,
              }
            : null,
        }
      : null,
    raw: toSerializable(line),
  };
}

function getCheckoutData({
  lines,
  discountCodes,
  shippingAddress,
  attributes,
  note,
  language,
  currency,
  country,
  activeStep,
}) {
  const cartLines = lines?.value ?? [];

  return {
    activeStep: activeStep?.handle || activeStep?.id || null,
    note: note || null,
    attributes: Array.isArray(attributes)
      ? attributes.map((attribute) => ({
          key: attribute.key,
          value: attribute.value,
        }))
      : [],
    discountCodes: Array.isArray(discountCodes)
      ? discountCodes.map((entry) => entry?.code).filter(Boolean)
      : [],
    language: language?.isoCode || language?.name || null,
    currency: currency?.isoCode || null,
    country: country?.isoCode || country?.name || null,
    shippingAddress: shippingAddress
      ? {
          firstName: shippingAddress.firstName || null,
          lastName: shippingAddress.lastName || null,
          company: shippingAddress.company || null,
          address1: shippingAddress.address1 || null,
          address2: shippingAddress.address2 || null,
          city: shippingAddress.city || null,
          provinceCode: shippingAddress.provinceCode || null,
          zip: shippingAddress.zip || null,
          countryCode: shippingAddress.countryCode || null,
          phone: shippingAddress.phone || null,
        }
      : null,
    lineCount: cartLines.length,
    lines: cartLines.map(serializeCartLine),
  };
}

// 1. Export the extension
export default function () {
  render(<Extension />, document.body);
}

async function logEvent(shop, level, eventType, message, payload = {}) {
  const backendUrl = String(BACKEND_URL_FROM_ENV || "")
    .trim()
    .replace(/\/$/, "");
  if (!backendUrl || !shop) return;

  try {
    await fetch(`${backendUrl}/api/app-event-log`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        shop,
        source: EXTENSION_SOURCE,
        extensionTarget: EXTENSION_TARGET,
        level,
        eventType,
        message,
        checkoutToken: shopify.checkoutToken?.value || null,
        ...getCustomerContext(),
        payload: {
          ...payload,
          checkoutData:
            payload.checkoutData ??
            getCheckoutData({
              lines: shopify.lines,
              discountCodes: shopify.discountCodes?.value,
              shippingAddress: shopify.shippingAddress?.value,
              attributes: shopify.attributes?.value,
              note: shopify.note?.value,
              language: shopify.localization?.language?.value,
              currency: shopify.currency?.value,
              country: shopify.localization?.country?.value,
              activeStep: shopify.buyerJourney?.activeStep?.value,
            }),
        },
      }),
    });
  } catch (error) {
    console.error("Upsell log failed", error);
  }
}

function Extension() {
  const { applyCartLinesChange, query, i18n } = shopify;
  useShippingAddress();
  useAttributes();
  useNote();
  useLanguage();
  useCurrency();
  useLocalizationCountry();
  useDiscountCodes();
  useBuyerJourneyActiveStep();
  useCheckoutToken();
  const settings = useSettings();
  const [upsellVariants, setUpsellVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingVariantId, setAddingVariantId] = useState(null);
  const [showError, setShowError] = useState(false);
  const { lines } = shopify;

  const cartLineIds =
    (lines?.value ?? [])
      .map((item) => item.merchandise?.id)
      .filter(Boolean)
      .join(",") || "";

  useEffect(() => {
    fetchUpsells();
  }, [cartLineIds]);

  useEffect(() => {
    if (showError) {
      const timer = setTimeout(() => setShowError(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showError]);

  async function handleAddToCart(variant) {
    setAddingVariantId(variant.id);
    const attributes = [{ key: "_source", value: "upsell-app" }];

    if (variant.ruleId != null) {
      attributes.push({
        key: "_upsell_rule_id",
        value: String(variant.ruleId),
      });
    }

    const result = await applyCartLinesChange({
      type: "addCartLine",
      merchandiseId: variant.id,
      quantity: 1,
      attributes,
    });
    setAddingVariantId(null);
    if (result.type === "error") {
      setShowError(true);
      void logEvent(
        shopify.shop.myshopifyDomain,
        "error",
        "upsell_add_error",
        result.message,
        {
          variantId: variant.id,
          ruleId: variant.ruleId,
          extensionTarget: EXTENSION_TARGET,
        },
      );
      console.error(result.message);
      return;
    }

    void logEvent(
      shopify.shop.myshopifyDomain,
      "info",
      "upsell_add_success",
      "Upsell variant added to cart.",
      {
        variantId: variant.id,
        ruleId: variant.ruleId,
        extensionTarget: EXTENSION_TARGET,
      },
    );
  }

  async function fetchUpsells() {
    setLoading(true);
    try {
      const cartLines = lines?.value ?? [];
      const cartVariantIds = cartLines
        .map((item) => item.merchandise?.id)
        .filter(Boolean);
      const cartProductIds = cartLines
        .map((item) => item.merchandise?.product?.id)
        .filter(Boolean);

      if (!cartVariantIds.length) {
        setUpsellVariants([]);
        setLoading(false);
        return;
      }

      const backendUrl = String(BACKEND_URL_FROM_ENV || "")
        .trim()
        .replace(/\/$/, "");
      const response = await fetch(`${backendUrl}/api/upsell/evaluate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          shop: shopify.shop.myshopifyDomain,
          cart_products: cartVariantIds,
          cart_product_ids: cartProductIds,
        }),
      });

      if (!response.ok) {
        console.error("Failed to load upsell products", await response.text());
        setUpsellVariants([]);
        setLoading(false);
        return;
      }

      const { upsells } = await response.json();

      if (!upsells || !upsells.length) {
        setUpsellVariants([]);
        setLoading(false);
        return;
      }

      const nodeIds = upsells
        .map((u) => u.variant_id || u.product_id)
        .filter(Boolean);
      if (!nodeIds.length) {
        setUpsellVariants([]);
        setLoading(false);
        return;
      }

      const { data } = await query(
        `query getUpsellVariants($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on ProductVariant {
              id
              title
              availableForSale
              price {
                amount
              }
              compareAtPrice {
                amount
              }
              product {
                title
                images(first: 1) {
                  nodes {
                    url
                  }
                }
              }
            }
            ... on Product {
              id
              variants(first: 1) {
                nodes {
                  id
                  title
                  availableForSale
                  price {
                    amount
                  }
                  compareAtPrice {
                    amount
                  }
                  product {
                    title
                    images(first: 1) {
                      nodes {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        }`,
        { variables: { ids: nodeIds } },
      );

      const rawNodes =
        data && typeof data === "object" && "nodes" in data ? data.nodes : null;
      const nodes = Array.isArray(rawNodes) ? rawNodes : [];
      const nodeById = {};
      for (const node of nodes) {
        if (node?.id) nodeById[node.id] = node;
      }

      const resolvedVariants = [];
      const resolvedUpsellMeta = [];

      for (const meta of upsells) {
        const id = meta.variant_id || meta.product_id;
        const node = id ? nodeById[id] : null;
        if (!node) continue;
        if (node.price != null) {
          if (node.availableForSale === false) continue;
          resolvedVariants.push(node);
          resolvedUpsellMeta.push(meta);
        } else {
          const variants = node.variants;
          const firstVariant =
            variants?.nodes?.[0] ?? variants?.edges?.[0]?.node;
          if (firstVariant && firstVariant.availableForSale !== false) {
            resolvedVariants.push(firstVariant);
            resolvedUpsellMeta.push(meta);
          }
        }
      }

      setUpsellVariants(
        resolvedVariants.map((variantNode, index) => ({
          id: variantNode.id,
          price: variantNode.price?.amount,
          compareAtPrice: variantNode.compareAtPrice?.amount ?? null,
          title: variantNode.product?.title,
          variantTitle:
            variantNode.title && variantNode.title !== "Default Title"
              ? variantNode.title
              : null,
          imageUrl:
            variantNode.product?.images?.nodes?.[0]?.url ??
            "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_medium.png?format=webp&v=1530129081",
          customMessage: resolvedUpsellMeta[index]?.custom_message ?? null,
          ruleId: resolvedUpsellMeta[index]?.rule_id ?? null,
        })),
      );
    } catch (error) {
      console.error(error);
      setUpsellVariants([]);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!upsellVariants.length) {
    return null;
  }

  return (
    <ProductOffers
      variants={upsellVariants}
      uiSettings={getExtensionUiSettings(settings)}
      i18n={i18n}
      addingVariantId={addingVariantId}
      handleAddToCart={handleAddToCart}
      showError={showError}
    />
  );
}

function LoadingSkeleton() {
  return (
    <s-stack gap="large-200">
      <s-divider />
      <s-heading>You might also like</s-heading>
      <s-stack gap="base">
        <s-grid
          gap="base"
          gridTemplateColumns="64px 1fr auto"
          alignItems="center"
        >
          <s-image loading="lazy" />
          <s-stack gap="none">
            <s-skeleton-paragraph />
            <s-skeleton-paragraph />
          </s-stack>
          <s-button variant="secondary" disabled={true}>
            Add
          </s-button>
        </s-grid>
      </s-stack>
    </s-stack>
  );
}

function ProductOffers({
  variants,
  uiSettings,
  i18n,
  addingVariantId,
  handleAddToCart,
  showError,
}) {
  return (
    <s-stack gap="large-200">
      <s-divider />
      {getHeadingType(uiSettings.headingFontSize) ? (
        <s-text type={getHeadingType(uiSettings.headingFontSize)}>
          {uiSettings.headingText}
        </s-text>
      ) : (
        <s-heading>{uiSettings.headingText}</s-heading>
      )}
      <s-scroll-box
        overflow="auto"
        maxBlockSize="360px"
        paddingInline="none"
        paddingBlock="none"
      >
        <s-stack gap="base">
          {variants.map((variant) => {
            const renderPrice = i18n.formatCurrency(variant.price);
            const compareAtPriceNumber = Number(variant.compareAtPrice);
            const priceNumber = Number(variant.price);
            const shouldShowCompareAtPrice =
              Number.isFinite(compareAtPriceNumber) &&
              Number.isFinite(priceNumber) &&
              compareAtPriceNumber > priceNumber;
            return (
              <s-grid
                key={variant.id}
                gap="base"
                gridTemplateColumns="64px 1fr auto"
                alignItems="center"
              >
                <s-image
                  borderWidth="base"
                  borderRadius="large-100"
                  src={variant.imageUrl}
                  alt={variant.title}
                  aspectRatio="1"
                />
                <s-stack gap="none">
                  <s-text type="strong">{variant.title}</s-text>
                  {variant.variantTitle && (
                    <s-text color="subdued">{variant.variantTitle}</s-text>
                  )}
                  <s-stack direction="inline" gap="small-200">
                    {shouldShowCompareAtPrice && (
                      <s-text color="subdued" type="redundant">
                        {i18n.formatCurrency(variant.compareAtPrice)}
                      </s-text>
                    )}
                    <s-text color="subdued">{renderPrice}</s-text>
                  </s-stack>
                  {variant.customMessage && (
                    <s-text color="subdued">{variant.customMessage}</s-text>
                  )}
                </s-stack>
                <s-button
                  variant={uiSettings.buttonVariant}
                  tone={uiSettings.buttonTone}
                  loading={addingVariantId === variant.id}
                  disabled={addingVariantId === variant.id}
                  accessibilityLabel={`Add ${variant.title} to cart`}
                  onClick={() => handleAddToCart(variant)}
                >
                  Add
                </s-button>
              </s-grid>
            );
          })}
        </s-stack>
      </s-scroll-box>
      {showError && <ErrorBanner />}
    </s-stack>
  );
}

function ErrorBanner() {
  return (
    <s-banner tone="critical">
      There was an issue adding this product. Please try again.
    </s-banner>
  );
}
